import {
  Apartment as ApartmentIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Layers as LayersIcon,
  MeetingRoom as MeetingRoomIcon,
} from '@mui/icons-material';
import { Box, Chip, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import React from 'react';
import { Building, Floor, Room } from '../../types';

interface BuildingTreeProps {
  buildings: Building[];
  floors: Floor[];
  rooms: Room[];
  onRoomSelect?: (room: Room) => void;
  onFloorSelect?: (floor: Floor) => void;
  onBuildingSelect?: (building: Building) => void;
  selectedRoomId?: string;
}

export const BuildingTree: React.FC<BuildingTreeProps> = ({
  buildings,
  floors,
  rooms,
  onRoomSelect,
  onFloorSelect,
  onBuildingSelect,
  selectedRoomId,
}) => {
  const getFloorsByBuilding = (buildingId: string): Floor[] => {
    return floors.filter((f) => f.buildingId === buildingId).sort((a, b) => a.level - b.level);
  };

  const getRoomsByFloor = (floorId: string): Room[] => {
    return rooms.filter((r) => r.floorId === floorId).sort((a, b) => a.roomNumber.localeCompare(b.roomNumber));
  };

  const getTotalRoomsForBuilding = (buildingId: string): number => {
    const buildingFloors = getFloorsByBuilding(buildingId);
    return buildingFloors.reduce((total, floor) => {
      return total + getRoomsByFloor(floor.id).length;
    }, 0);
  };

  return (
    <SimpleTreeView sx={{ flexGrow: 1, maxWidth: 400, overflowY: 'auto' }}>
      {buildings.map((building) => {
        const buildingFloors = getFloorsByBuilding(building.id);
        const totalRooms = getTotalRoomsForBuilding(building.id);

        return (
          <TreeItem
            key={building.id}
            itemId={building.id}
            label={
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}
                onClick={() => onBuildingSelect?.(building)}
              >
                <ApartmentIcon color="primary" />
                <Typography variant="body1" fontWeight="bold">
                  {building.name}
                </Typography>
                <Chip label={`${totalRooms} rooms`} size="small" />
              </Box>
            }
          >
            {buildingFloors.map((floor) => {
              const floorRooms = getRoomsByFloor(floor.id);

              return (
                <TreeItem
                  key={floor.id}
                  itemId={floor.id}
                  label={
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
                      onClick={() => onFloorSelect?.(floor)}
                    >
                      <LayersIcon fontSize="small" color="action" />
                      <Typography variant="body2">{floor.name}</Typography>
                      <Chip label={`${floorRooms.length} rooms`} size="small" variant="outlined" />
                    </Box>
                  }
                >
                  {floorRooms.map((room) => (
                    <TreeItem
                      key={room.id}
                      itemId={room.id}
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.5,
                            backgroundColor: selectedRoomId === room.id ? 'action.selected' : 'transparent',
                            borderRadius: 1,
                            px: 1,
                          }}
                          onClick={() => onRoomSelect?.(room)}
                        >
                          <MeetingRoomIcon fontSize="small" />
                          <Typography variant="body2">{room.roomNumber}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {room.name}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </TreeItem>
              );
            })}
          </TreeItem>
        );
      })}
    </SimpleTreeView>
  );
};

export default BuildingTree;
